/**
 * Client-side rate limiter using in-memory store.
 * Prevents rapid repeated submissions on payment and exam endpoints.
 */

interface RateLimitEntry {
  count: number;
  resetAt: number;
}

const store = new Map<string, RateLimitEntry>();

/**
 * Check if an action is rate-limited.
 * @param key     Unique key (e.g. "payment:user@email.com")
 * @param limit   Max allowed attempts in the window
 * @param windowMs Time window in milliseconds (default 60s)
 * @returns { allowed: boolean; remainingMs: number }
 */
export function checkRateLimit(
  key: string,
  limit: number,
  windowMs = 60_000
): { allowed: boolean; remainingMs: number } {
  const now = Date.now();
  const entry = store.get(key);

  if (!entry || now > entry.resetAt) {
    store.set(key, { count: 1, resetAt: now + windowMs });
    return { allowed: true, remainingMs: 0 };
  }

  if (entry.count >= limit) {
    return { allowed: false, remainingMs: entry.resetAt - now };
  }

  entry.count += 1;
  return { allowed: true, remainingMs: 0 };
}

/** Format remaining seconds for user-facing messages */
export function formatRemainingTime(ms: number): string {
  const seconds = Math.ceil(ms / 1000);
  if (seconds < 60) return `${seconds} second${seconds !== 1 ? 's' : ''}`;
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
}
